import React, { useMemo, useRef, useState, useCallback, useEffect } from "react";
import "../../styles/Sleepchart.css";

const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;
function toIST(ms) { return new Date(ms + IST_OFFSET_MS); }
function msToISTDateKey(ms) {
  const d = toIST(ms);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth()+1).padStart(2,"0")}-${String(d.getUTCDate()).padStart(2,"0")}`;
}
function fmtHHMM_IST(ms) {
  const d = toIST(ms);
  return `${String(d.getUTCHours()).padStart(2,"0")}:${String(d.getUTCMinutes()).padStart(2,"0")}`;
}
function fmtHHMMSS_IST(ms) {
  const d = toIST(ms);
  return `${String(d.getUTCHours()).padStart(2,"0")}:${String(d.getUTCMinutes()).padStart(2,"0")}:${String(d.getUTCSeconds()).padStart(2,"0")}`;
}
function fmt12h_IST(ms) {
  const d = toIST(ms);
  let h = d.getUTCHours();
  const min = String(d.getUTCMinutes()).padStart(2,"0");
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${h}:${min} ${ampm}`;
}
function fmt12hSec_IST(ms) {
  const d = toIST(ms);
  let h = d.getUTCHours();
  const min  = String(d.getUTCMinutes()).padStart(2,"0");
  const sec  = String(d.getUTCSeconds()).padStart(2,"0");
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${h}:${min}:${sec} ${ampm}`;
}

const STAGE_CFG = {
  awake:       { label: "Awake",              color: "#FFB74D", band: 0 },
  rem_sleep:   { label: "Rapid Eye Movement", color: "#C084FC", band: 1 },
  light_sleep: { label: "Light sleep",        color: "#A78BFA", band: 2 },
  deep_sleep:  { label: "Deep sleep",         color: "#6366F1", band: 3 },
};
const STAGE_BAR_H = { awake: 14, rem_sleep: 26, light_sleep: 36, deep_sleep: 36 };
const CHART_H   = 160;
const BAND_H    = CHART_H / 4;
const Y_LABEL_W = 36;
const MIN_ZOOM  = 1;
const MAX_ZOOM  = 60; // 60× zoom = can see individual seconds

function normaliseStage(raw) {
  if (!raw) return "light_sleep";
  switch (raw) {
    case "rem":       return "rem_sleep";
    case "awake":     return "awake";
    case "non_sleep": return "awake";
    case "unknown":   return "light_sleep";
    default:          return raw;
  }
}
function toMs(ts) { if (!ts) return 0; return ts < 1e12 ? ts * 1000 : ts; }
function secToHMStr(sec) {
  const t = Math.round(sec/60), h = Math.floor(t/60), m = t%60;
  if (h===0) return `${m}m`; if (m===0) return `${h}h`; return `${h}h ${m}m`;
}
function secToHMParts(sec) { const t=Math.round(sec/60); return {h:Math.floor(t/60),m:t%60}; }
function itemDateKey(item) { return msToISTDateKey(toMs(item.ts)); }
function anchorToISTKey(anchor) {
  const d = new Date(anchor);
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
}

const SOURCE_PRIORITY = { jcband: 0, jcring: 0, qring: 1 };
function pickBestSource(items) {
  if (!items?.length) return [];
  const bySource = {};
  items.forEach(item => {
    const src = (item.source || "unknown").toLowerCase();
    if (!bySource[src]) bySource[src] = [];
    bySource[src].push(item);
  });
  const sources = Object.keys(bySource);
  if (sources.length === 1) return items;
  sources.sort((a, b) => (SOURCE_PRIORITY[a] ?? 99) - (SOURCE_PRIORITY[b] ?? 99));
  return bySource[sources[0]];
}

function buildSegments(graph) {
  if (!graph?.length) return [];
  const segments = [];
  const sorted = graph
    .map(s => ({ stage: normaliseStage(s.stage), startMs: toMs(s.start), endMs: toMs(s.end) }))
    .filter(s => s.startMs > 0 && s.endMs > s.startMs)
    .sort((a, b) => a.startMs - b.startMs);

  let current = { ...sorted[0] };
  for (let i = 1; i < sorted.length; i++) {
    const prev = sorted[i - 1];
    const next = sorted[i];
    const gap  = next.startMs - prev.endMs;
    if (gap > 60 * 1000) { segments.push(current); current = { ...next }; continue; }
    if (next.stage === current.stage) { current.endMs = next.endMs; }
    else { segments.push(current); current = { ...next }; }
  }
  segments.push(current);
  return segments;
}

// ─── Smart x-axis tick generator ─────────────────────────────────────────────
// Picks a human-readable interval based on how many ms are visible.
function buildXTicks(visStartMs, visEndMs, plotW) {
  const spanMs  = visEndMs - visStartMs;
  const minPxGap = 60; // minimum px between ticks
  const maxTicks = Math.floor(plotW / minPxGap);

  // candidate intervals in ms
  const candidates = [
    1000, 5000, 10000, 15000, 30000,
    60000, 2*60000, 5*60000, 10*60000, 15*60000, 30*60000,
    60*60000, 2*60*60000
  ];
  const interval = candidates.find(c => Math.floor(spanMs / c) <= maxTicks) || candidates[candidates.length - 1];

  const ticks = [];
  const first = Math.ceil(visStartMs / interval) * interval;
  for (let t = first; t <= visEndMs; t += interval) {
    const x = ((t - visStartMs) / spanMs) * plotW;
    const label = interval < 60000 ? fmt12hSec_IST(t) : fmt12h_IST(t);
    ticks.push({ ms: t, x, label });
  }
  return ticks;
}

/* ─── Day hypnogram with zoom + pan ─────────────────────────────── */
function DayHypnogram({ segments, rangeStartMs, rangeEndMs, stats }) {
  const wrapRef   = useRef(null);
  const svgRef    = useRef(null);
  const [svgW, setSvgW]       = useState(0);
  const [hovered, setHov]     = useState(null);

  // Zoom state: zoom level + pan offset as fraction [0,1] of the full range
  const [zoom,    setZoom]    = useState(1);     // 1 = full view
  const [panFrac, setPanFrac] = useState(0);     // 0 = leftmost

  // drag state refs (avoid re-renders during drag)
  const dragRef = useRef({ active: false, startX: 0, startPan: 0 });
  // pinch state refs
  const pinchRef = useRef({ active: false, startDist: 0, startZoom: 1, startPanFrac: 0, midFrac: 0 });

  useEffect(() => {
    if (!wrapRef.current) return;
    const ro = new ResizeObserver(([e]) => setSvgW(Math.floor(e.contentRect.width)));
    ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, []);

  const fullSpanMs = rangeEndMs - rangeStartMs || 1;
  const plotW      = Math.max(0, svgW - Y_LABEL_W);

  // Clamp pan so window never goes outside the full range
  const clampPan = useCallback((p, z) => {
    const windowFrac = 1 / z;
    return Math.max(0, Math.min(1 - windowFrac, p));
  }, []);

  // Derived visible window in ms
  const windowFrac  = 1 / zoom;
  const clampedPan  = clampPan(panFrac, zoom);
  const visStartMs  = rangeStartMs + clampedPan * fullSpanMs;
  const visEndMs    = visStartMs + windowFrac * fullSpanMs;
  const visSpanMs   = visEndMs - visStartMs;

  // Convert ms → plotX (px relative to left of plot area)
  const msToX = useCallback((ms) => {
    return ((ms - visStartMs) / visSpanMs) * plotW;
  }, [visStartMs, visSpanMs, plotW]);

  const barData = useMemo(() => {
    if (!plotW || !segments.length) return [];
    return segments
      .filter(seg => seg.endMs > visStartMs && seg.startMs < visEndMs)
      .map(seg => {
        const cfg   = STAGE_CFG[seg.stage] || STAGE_CFG.light_sleep;
        const barH  = Math.min(STAGE_BAR_H[seg.stage] || 26, BAND_H - 2);
        const xL    = Y_LABEL_W + Math.max(0, msToX(seg.startMs));
        const xR    = Y_LABEL_W + Math.min(plotW, msToX(seg.endMs));
        const w     = Math.max(2, xR - xL);
        const y     = cfg.band * BAND_H + (BAND_H - barH) / 2;
        const cx    = xL + w / 2;
        const cy    = y + barH / 2;
        return { seg, cfg, xL, w, y, barH, cx, cy };
      });
  }, [segments, plotW, visStartMs, visEndMs, msToX]);

  const connLines = useMemo(() => {
    if (barData.length < 2) return null;
    return barData.slice(0, -1).map((a, i) => {
      const b = barData[i + 1];
      return (
        <polyline
          key={`cl${i}`}
          points={`${a.xL+a.w},${a.cy} ${a.xL+a.w},${b.cy} ${b.xL},${b.cy}`}
          fill="none" stroke={a.cfg.color} strokeWidth="1"
          strokeOpacity="0.4" strokeLinejoin="round"
        />
      );
    });
  }, [barData]);

  const yLabels = useMemo(() => {
    if (!svgW) return null;
    return ["Awake", "REM", "Light", "Deep"].map((lbl, band) => (
      <text key={lbl} x={Y_LABEL_W - 4} y={band * BAND_H + BAND_H / 2 + 4}
        fontSize="9" fill="rgba(255,255,255,0.38)" fontFamily="system-ui" textAnchor="end">
        {lbl}
      </text>
    ));
  }, [svgW]);

  const dividers = useMemo(() => {
    if (!svgW) return null;
    return [1, 2, 3].map(i => (
      <line key={`d${i}`} x1={Y_LABEL_W} y1={i * BAND_H} x2={svgW} y2={i * BAND_H}
        stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
    ));
  }, [svgW]);

  // X-axis ticks
  const xTicks = useMemo(() => {
    if (!plotW) return [];
    return buildXTicks(visStartMs, visEndMs, plotW);
  }, [visStartMs, visEndMs, plotW]);

  // ── Hover ─────────────────────────────────────────────────────────────────
  const handleMove = useCallback(e => {
    if (!wrapRef.current || !plotW || !barData.length) return;
    if (dragRef.current.active) return; // don't show tooltip while dragging
    const rect    = wrapRef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const px      = Math.max(Y_LABEL_W, Math.min(svgW, clientX - rect.left));
    const hit     = barData.find(bd => px >= bd.xL && px < bd.xL + bd.w);
    setHov(hit ? { seg: hit.seg, cfg: hit.cfg, xPx: hit.cx } : null);
  }, [barData, plotW, svgW]);

  // ── Wheel zoom ────────────────────────────────────────────────────────────
  const handleWheel = useCallback(e => {
    e.preventDefault();
    const rect     = svgRef.current?.getBoundingClientRect();
    if (!rect) return;
    const mouseX   = e.clientX - rect.left - Y_LABEL_W;
    const mouseFrac = clampedPan + (mouseX / plotW) * windowFrac; // fraction under cursor

    const delta    = e.deltaY > 0 ? 0.9 : 1.1;
    const newZoom  = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, zoom * delta));
    const newWinFrac = 1 / newZoom;
    // keep the fraction under cursor fixed
    const newPan   = clampPan(mouseFrac - (mouseX / plotW) * newWinFrac, newZoom);

    setZoom(newZoom);
    setPanFrac(newPan);
    setHov(null);
  }, [zoom, clampedPan, windowFrac, plotW, clampPan]);

  // ── Mouse drag pan ────────────────────────────────────────────────────────
  const handleMouseDown = useCallback(e => {
    if (e.button !== 0) return;
    dragRef.current = { active: true, startX: e.clientX, startPan: clampedPan };
    setHov(null);
  }, [clampedPan]);

  const handleMouseMoveDrag = useCallback(e => {
    if (!dragRef.current.active) return;
    const dx       = e.clientX - dragRef.current.startX;
    const dFrac    = -(dx / plotW) * windowFrac;
    const newPan   = clampPan(dragRef.current.startPan + dFrac, zoom);
    setPanFrac(newPan);
  }, [plotW, windowFrac, zoom, clampPan]);

  const handleMouseUp = useCallback(() => {
    dragRef.current.active = false;
  }, []);

  // ── Pinch zoom (touch) ────────────────────────────────────────────────────
  const handleTouchStart = useCallback(e => {
    if (e.touches.length === 2) {
      e.preventDefault();
      const t1 = e.touches[0], t2 = e.touches[1];
      const dist  = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
      const midX  = (t1.clientX + t2.clientX) / 2;
      const rect  = svgRef.current?.getBoundingClientRect();
      const relX  = midX - (rect?.left || 0) - Y_LABEL_W;
      const midFrac = clampedPan + (relX / plotW) * windowFrac;
      pinchRef.current = { active: true, startDist: dist, startZoom: zoom,
                           startPanFrac: clampedPan, midFrac };
    } else if (e.touches.length === 1) {
      dragRef.current = { active: true, startX: e.touches[0].clientX, startPan: clampedPan };
      setHov(null);
    }
  }, [zoom, clampedPan, windowFrac, plotW]);

  const handleTouchMove = useCallback(e => {
    if (e.touches.length === 2 && pinchRef.current.active) {
      e.preventDefault();
      const t1 = e.touches[0], t2 = e.touches[1];
      const dist    = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
      const scale   = dist / pinchRef.current.startDist;
      const newZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, pinchRef.current.startZoom * scale));
      const newWinFrac = 1 / newZoom;
      const midX    = (t1.clientX + t2.clientX) / 2;
      const rect    = svgRef.current?.getBoundingClientRect();
      const relX    = midX - (rect?.left || 0) - Y_LABEL_W;
      const newPan  = clampPan(pinchRef.current.midFrac - (relX / plotW) * newWinFrac, newZoom);
      setZoom(newZoom);
      setPanFrac(newPan);
    } else if (e.touches.length === 1 && dragRef.current.active) {
      const dx    = e.touches[0].clientX - dragRef.current.startX;
      const dFrac = -(dx / plotW) * windowFrac;
      setPanFrac(clampPan(dragRef.current.startPan + dFrac, zoom));
    }
  }, [plotW, windowFrac, zoom, clampPan]);

  const handleTouchEnd = useCallback(e => {
    if (e.touches.length < 2) pinchRef.current.active = false;
    if (e.touches.length === 0) dragRef.current.active = false;
  }, []);

  // Attach wheel with { passive: false } so we can preventDefault
  useEffect(() => {
    const el = svgRef.current;
    if (!el) return;
    el.addEventListener("wheel", handleWheel, { passive: false });
    return () => el.removeEventListener("wheel", handleWheel);
  }, [handleWheel]);

  // Reset zoom
  const resetZoom = useCallback(() => { setZoom(1); setPanFrac(0); setHov(null); }, []);

  // Hero / dominant
  function getDominantSegment(segs) {
    let max = null;
    segs.forEach(s => { const d = s.endMs - s.startMs; if (!max || d > (max.endMs - max.startMs)) max = s; });
    return max;
  }
  const dominantSeg = getDominantSegment(segments);
  const heroStage   = hovered ? hovered.seg.stage : dominantSeg?.stage;
  const heroSec     = hovered
    ? Math.round((hovered.seg.endMs - hovered.seg.startMs) / 1000)
    : dominantSeg ? Math.round((dominantSeg.endMs - dominantSeg.startMs) / 1000) : 0;
  const heroRange   = hovered
    ? `${fmtHHMMSS_IST(hovered.seg.startMs)} – ${fmtHHMMSS_IST(hovered.seg.endMs)}`
    : dominantSeg ? `${fmtHHMM_IST(dominantSeg.startMs)} – ${fmtHHMM_IST(dominantSeg.endMs)}` : "";
  const heroCfg   = STAGE_CFG[heroStage] || STAGE_CFG.light_sleep;
  const heroParts = secToHMParts(heroSec);
  const ttLeft    = hovered ? Math.max(60, Math.min(hovered.xPx || 0, svgW - 60)) : 0;

  const isZoomed = zoom > 1.01;

  return (
    <div className="sc-day-wrap">
      <div className="sc-hero">
        <span className="sc-hero-label" style={{ color: heroCfg.color }}>{heroCfg.label}</span>
        <span className="sc-hero-num">
          {heroParts.h > 0 && <><strong>{heroParts.h}</strong><span className="sc-hero-unit">H </span></>}
          <strong>{heroParts.m}</strong><span className="sc-hero-unit">M</span>
        </span>
      </div>
      {heroRange && <div className="sc-deep-range">{heroRange}</div>}

      <div className="sc-legend">
        {Object.entries(STAGE_CFG).map(([k, c]) => (
          <div key={k} className="sc-legend-item">
            <span className="sc-dot" style={{ background: c.color }} />
            <span className="sc-leg-lbl">{c.label}</span>
          </div>
        ))}
      </div>

      {/* Zoom hint + reset button */}
      <div className="sc-zoom-bar">
        <span className="sc-zoom-hint">
          {isZoomed
            ? `${zoom.toFixed(1)}× — drag to pan`
            : "Scroll or pinch to zoom"}
        </span>
        {isZoomed && (
          <button className="sc-zoom-reset" onClick={resetZoom}>Reset</button>
        )}
      </div>

      {/* Chart canvas */}
      <div
        ref={wrapRef}
        className="sc-canvas"
        style={{ cursor: isZoomed ? "grab" : "crosshair" }}
        onMouseMove={e => { handleMouseMoveDrag(e); handleMove(e); }}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseLeave={() => { handleMouseUp(); setHov(null); }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <svg
          ref={svgRef}
          width={svgW || "100%"}
          height={CHART_H}
          style={{ display: "block", overflow: "visible", userSelect: "none" }}
        >
          <defs>
            <clipPath id="sc-plot-clip">
              <rect x={Y_LABEL_W} y={0} width={plotW} height={CHART_H} />
            </clipPath>
          </defs>

          {/* Y-axis labels — outside clip */}
          {yLabels}

          {/* Plot area */}
          <g clipPath="url(#sc-plot-clip)">
            {dividers}
            {connLines}
            {barData.map((bd, i) => (
              <rect key={`b${i}`} x={bd.xL} y={bd.y} width={bd.w} height={bd.barH}
                fill={bd.cfg.color} rx="3" ry="3" className="sc-vbar"
                style={{ animationDelay: `${i * 4}ms` }} />
            ))}
            {hovered && (
              <line x1={hovered.xPx} y1={0} x2={hovered.xPx} y2={CHART_H}
                stroke="rgba(180,160,255,0.3)" strokeWidth="1.5" strokeDasharray="3 3" />
            )}
          </g>
        </svg>

        {hovered && (
          <div className="sc-tooltip" style={{ left: ttLeft, borderLeftColor: hovered.cfg.color }}>
            <span className="sc-tt-stage" style={{ color: hovered.cfg.color }}>{hovered.cfg.label}</span>
            <span className="sc-tt-sep">·</span>
            <span className="sc-tt-dur">{secToHMStr(Math.round((hovered.seg.endMs - hovered.seg.startMs) / 1000))}</span>
            <span className="sc-tt-sep">·</span>
            <span className="sc-tt-time">{fmtHHMMSS_IST(hovered.seg.startMs)} – {fmtHHMMSS_IST(hovered.seg.endMs)}</span>
          </div>
        )}
      </div>

      {/* Dynamic x-axis with ticks */}
      <div className="sc-x-axis-zoom" style={{ paddingLeft: Y_LABEL_W }}>
        <div className="sc-x-ticks" style={{ position: "relative", height: 20, width: plotW }}>
          {xTicks.map((tick, i) => (
            <span
              key={i}
              className="sc-x-tick-label"
              style={{ left: tick.x }}
            >
              {tick.label}
            </span>
          ))}
        </div>
      </div>

      <div className="sc-totals">
        <div className="sc-total-row">
          <span className="sc-total-lbl">Total sleep</span>
          <strong className="sc-total-val">{secToHMStr(stats.totalSec)}</strong>
        </div>
        <div className="sc-breakdown">
          {[["deep_sleep","Deep",stats.deepSec],["light_sleep","Light",stats.shallowSec],
            ["rem_sleep","REM",stats.rapidSec],["awake","Awake",stats.awakeSec]].map(([k, l, s]) =>
            s > 0 ? (
              <span key={k} className="sc-pill" style={{
                borderColor: STAGE_CFG[k]?.color + "55",
                color: STAGE_CFG[k]?.color,
                background: STAGE_CFG[k]?.color + "18"
              }}>
                <span className="sc-pill-dot" style={{ background: STAGE_CFG[k]?.color }} />{l} · {secToHMStr(s)}
              </span>
            ) : null
          )}
        </div>
      </div>
    </div>
  );
}

function AggView({ entries }) {
  const maxSec = Math.max(...entries.map(e => e.totalSec), 1);
  return (
    <div className="sc-agg-wrap">
      {entries.map((day, i) => (
        <div key={i} className="sc-agg-row">
          <span className="sc-agg-day">{day.label}</span>
          <div className="sc-agg-track">
            <div className="sc-agg-fill" style={{ width: `${(day.totalSec / maxSec) * 100}%` }}>
              {day.segments.filter(s => s.stage !== "awake").map((seg, j) => {
                const pct = ((seg.endMs - seg.startMs) / 1000 / (day.totalSec || 1)) * 100;
                return (
                  <div key={j} className="sc-agg-seg" style={{
                    width: `${Math.max(pct, 1)}%`,
                    background: (STAGE_CFG[seg.stage] || STAGE_CFG.light_sleep).color
                  }} />
                );
              })}
            </div>
          </div>
          <span className="sc-agg-dur">{day.totalSec > 0 ? secToHMStr(day.totalSec) : "—"}</span>
        </div>
      ))}
    </div>
  );
}

export default function SleepChart({ rawSleepItems, anchor, viewMode = "day" }) {

  const sleepItemsByDate = useMemo(() => {
    if (!rawSleepItems?.length) return {};
    const raw = {};
    rawSleepItems.forEach(item => {
      if (item.type !== "sleep" || !item.sleep?.sleep_graph?.length) return;
      const key = itemDateKey(item);
      if (!raw[key]) raw[key] = [];
      raw[key].push(item);
    });
    const map = {};
    Object.entries(raw).forEach(([date, items]) => {
      map[date] = pickBestSource(items);
    });
    return map;
  }, [rawSleepItems]);

  const dayData = useMemo(() => {
    if (viewMode !== "day") return null;
    const items = sleepItemsByDate[anchorToISTKey(anchor)];
    if (!items?.length) return null;

    let allSegs = [], totalSec = 0, deepSec = 0, shallowSec = 0, rapidSec = 0, awakeSec = 0;
    items.forEach(item => {
      const sl = item.sleep;
      totalSec   += sl.total_sleep_duration || 0;
      deepSec    += sl.deep_duration        || 0;
      shallowSec += sl.shallow_duration     || 0;
      rapidSec   += sl.rapid_duration       || 0;
      awakeSec   += sl.awake_duration       || 0;
      allSegs     = allSegs.concat(buildSegments(sl.sleep_graph));
    });
    if (!allSegs.length) return null;
    allSegs.sort((a, b) => a.startMs - b.startMs);

    const byStage = { awake: 0, rem_sleep: 0, light_sleep: 0, deep_sleep: 0 };
    allSegs.forEach(s => {
      const d = Math.round((s.endMs - s.startMs) / 1000);
      if (s.stage in byStage) byStage[s.stage] += d;
    });
    return {
      segments:     allSegs,
      rangeStartMs: allSegs[0].startMs,
      rangeEndMs:   allSegs[allSegs.length - 1].endMs,
      stats:        { totalSec, deepSec, shallowSec, rapidSec, awakeSec, byStage },
    };
  }, [sleepItemsByDate, anchor, viewMode]);

  const aggData = useMemo(() => {
    if (viewMode === "day" || !Object.keys(sleepItemsByDate).length) return null;
    const anchorKey  = anchorToISTKey(anchor);
    const [aY, aM]   = anchorKey.split("-").map(Number);
    const anchorDate = new Date(anchor);
    const wkStart    = new Date(anchorDate);
    wkStart.setDate(wkStart.getDate() - wkStart.getDay());
    wkStart.setHours(0, 0, 0, 0);
    const wkEnd = new Date(wkStart);
    wkEnd.setDate(wkEnd.getDate() + 7);

    return Object.entries(sleepItemsByDate)
      .filter(([ds]) => {
        const [y, m] = ds.split("-").map(Number);
        if (viewMode === "week") { const d = new Date(ds + "T00:00:00"); return d >= wkStart && d < wkEnd; }
        return y === aY && m === aM;
      })
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([ds, items]) => {
        let allSegs = [], totalSec = 0;
        const label = new Date(ds + "T00:00:00").toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
        items.forEach(item => {
          totalSec += item.sleep.total_sleep_duration || 0;
          allSegs   = allSegs.concat(buildSegments(item.sleep.sleep_graph));
        });
        allSegs.sort((a, b) => a.startMs - b.startMs);
        return { label, segments: allSegs, totalSec };
      });
  }, [sleepItemsByDate, viewMode, anchor]);

  const isEmpty = viewMode === "day" ? !dayData : !aggData?.length;

  return (
    <div className="sc-card">
      {isEmpty
        ? <div className="sc-empty">No sleep data for this {viewMode}.</div>
        : viewMode === "day"
          ? <DayHypnogram {...dayData} />
          : <AggView entries={aggData} />
      }
    </div>
  );
}